import React, { useMemo, useState } from 'react';
import { MarketEmptyState, MarketSkeletonBlock } from './MarketEmptyState';

/**
 * Generic dark, compact, sortable data table for Market.
 *
 * @param {Array}    columns    - [{ key, label, align, sortable, render(row), sortValue(row) }]
 * @param {Array}    rows
 * @param {Function} getRowKey  - (row, idx) => string
 * @param {boolean}  loading
 * @param {string}   emptyTitle
 * @param {string}   emptyMessage
 * @param {string}   initialSortKey
 * @param {'asc'|'desc'} initialSortDir
 */
export const MarketDataTable = ({
  columns,
  rows,
  getRowKey = (row, idx) => row?.id || idx,
  loading = false,
  emptyIcon = '📭',
  emptyTitle = 'No data yet',
  emptyMessage = 'Market data will appear once more races are settled.',
  initialSortKey = null,
  initialSortDir = 'desc',
}) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDir, setSortDir] = useState(initialSortDir);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows || [];
    const col = columns.find(c => c.key === sortKey);
    if (!col) return rows || [];
    const accessor = col.sortValue || (r => r[col.key]);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...(rows || [])].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, sortKey, sortDir, columns]);

  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('desc');
    }
  };

  if (loading) return <MarketSkeletonBlock height={260} />;
  if (!rows || rows.length === 0) {
    return <MarketEmptyState icon={emptyIcon} title={emptyTitle} message={emptyMessage} />;
  }

  return (
    <div className="mk-table-scroll">
      <table className="mk-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={`mk-table-th mk-table-th--${col.align || 'left'} ${col.sortable ? 'mk-table-th--sortable' : ''}`}
                onClick={() => handleSort(col)}
              >
                {col.label}
                {col.sortable && sortKey === col.key && (
                  <span className="mk-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr key={getRowKey(row, idx)} className="mk-table-row">
              {columns.map(col => (
                <td key={col.key} className={`mk-table-td mk-table-td--${col.align || 'left'}`}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MarketDataTable;
